import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Users,
  Search,
  Filter,
  TrendingUp,
  BookOpen,
  Award,
  Calendar,
  ChevronRight,
  ArrowUpDown,
  Download,
  User,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Briefcase,
  CheckCircle2,
  AlertCircle,
  Clock,
  BarChart3,
  Target,
  Plus,
  RefreshCw,
  Zap,
  ArrowLeft,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import SummaryCard from '../../components/Common/SummaryCard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import useGlobalSearch from '../../hooks/useGlobalSearch';

const isCompletedCourseEnrollment = (enrollment = {}) => (
  enrollment.isCompleted === true ||
  enrollment.status === 'completed' ||
  enrollment.displayStatus === 'completed' ||
  enrollment.assessmentPassed === true ||
  (enrollment.learningFlowCompleted === true && Number(enrollment.progress || 0) >= 100) ||
  Number(enrollment.progress || 0) >= 100
);

const getAssessmentScore = (assessment = {}) => (
  assessment.score ?? assessment.percentage ?? assessment.totalWeightedScore ?? assessment.bestAssessmentScore
);

const getAssessmentName = (assessment = {}) => (
  assessment.assessment?.assessmentName ||
  assessment.assessmentName ||
  assessment.course?.title ||
  'Unknown Assessment'
);

const StudentProgress = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const { user } = useAuth();
  const [colleges, setColleges] = useState([]);
  const [showDetailView, setShowDetailView] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [viewMode, setViewMode] = useState('student');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);


  // Search and filter state
  const { searchQuery, setSearchQuery, debouncedSearchQuery, searchInputRef } = useGlobalSearch('', 400, () => setPage(1));
  const [filters, setFilters] = useState({
    institute: '',
    department: '',
    batch: '',
    status: '',
    minReadiness: '',
    maxReadiness: ''
  });
  const [sortBy, setSortBy] = useState('readinessScore');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const queryObj = {
        page,
        limit: 20,
        sortBy,
        sortOrder,
        search: debouncedSearchQuery
      };
      
      if (filters.institute) queryObj.collegeId = filters.institute;
      if (filters.department) queryObj.department = filters.department;
      if (filters.batch) queryObj.batch = filters.batch;
      if (filters.status) queryObj.status = filters.status;

      const params = new URLSearchParams(queryObj);

      const response = await api.get(`/student-progress/students?${params}`);
      if (response.data.success) {
        setStudents(response.data.data);
        setTotalPages(Math.ceil(response.data.total / 20));
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load student progress data');
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder, debouncedSearchQuery, filters]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const qs = filters.institute ? `?collegeId=${filters.institute}` : '';
      const [deptRes, batchRes] = await Promise.all([
        api.get(`/student-progress/analytics/department-wise${qs}`),
        api.get(`/student-progress/analytics/batch-wise${qs}`)
      ]);

      if (deptRes.data.success && batchRes.data.success) {
        setAnalytics({
          department: deptRes.data.data,
          batch: batchRes.data.data
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [filters.institute]);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoadingLeaderboard(true);
      const response = await api.get('/student-progress/analytics/college-leaderboard');
      if (response.data.success) {
        setLeaderboard(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load college leaderboard');
    } finally {
      setLoadingLeaderboard(false);
    }
  }, []);

  const fetchColleges = useCallback(async () => {
    if (user?.role === 'admin') {
      try {
        const response = await api.get('/colleges');
        if (response.data.success) {
          setColleges(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching colleges:', error);
      }
    }
  }, [user]);
















  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  useEffect(() => {
    if (viewMode === 'student') {
      fetchStudents();
      fetchAnalytics();
    } else {
      fetchLeaderboard();
    }
  }, [fetchStudents, fetchAnalytics, fetchLeaderboard, viewMode]);





  // fetchColleges called on mount




  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setShowDetailView(true);
  };

  const handleCalculateReadiness = async (studentId) => {
    try {
      await api.post(`/student-progress/students/${studentId}/calculate-readiness`);
      toast.success('Readiness score calculated successfully');
      fetchStudents();
    } catch (error) {
      toast.error('Failed to calculate readiness score');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getReadinessColor = (score) => {
    if (score >= 75) return 'bg-emerald-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  const getReadinessLabel = (score) => {
    if (score >= 75) return 'High';
    if (score >= 50) return 'Medium';
    return 'Low';
  };

  if (loading && students.length === 0) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center space-y-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 border-4 border-amber-100 border-t-amber-600 rounded-full"
        />
        <p className="text-gray-400 font-medium animate-pulse">Loading Student Progress...</p>
      </div>
    );
  }

  const renderLeaderboard = () => {
    if (loadingLeaderboard && leaderboard.length === 0) {
      return (
        <div className="min-h-[300px] flex flex-col items-center justify-center space-y-4 bg-white dark:bg-[#002147] border border-slate-200/80 dark:border-[#1a3884]/25 rounded-xl animate-pulse">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-10 h-10 border-4 border-amber-100 border-t-amber-600 rounded-full"
          />
          <p className="text-gray-450 font-medium">Loading Leaderboard...</p>
        </div>
      );
    }

    if (leaderboard.length === 0) {
      return (
        <div className="min-h-[200px] flex items-center justify-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#002147] border border-slate-200/80 dark:border-[#1a3884]/25 rounded-xl">
          No college leaderboard data available.
        </div>
      );
    }

    return (
      <Card className="shadow-sm overflow-hidden bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-xl [&>div]:p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] table-fixed text-sm">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[24%]" />
              <col className="w-[18%]" />
              <col className="w-[10%]" />
              <col className="w-[11%]" />
              <col className="w-[11%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="bg-slate-50 dark:bg-[#001f40]/50 border-b border-gray-200 dark:border-[#1a3884]/30">
              <tr>
                <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Rank</th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">College</th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Adoption Score</th>
                <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Students</th>
                <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Course Comp.</th>
                <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Avg Engagement</th>
                <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Readiness</th>
                <th className="px-6 py-3.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Placements</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-[#1a3884]/20">
              {leaderboard.map((item, idx) => {
                const rank = idx + 1;
                let rankBadge = rank;
                if (rank === 1) rankBadge = "🥇";
                else if (rank === 2) rankBadge = "🥈";
                else if (rank === 3) rankBadge = "🥉";

                return (
                  <tr key={item.collegeId} className="hover:bg-slate-50/50 dark:hover:bg-[#1a3884]/10 transition-colors">
                    <td className="px-6 py-3.5 text-center align-middle font-bold text-slate-700 dark:text-slate-300">
                      <span className="text-base">{rankBadge}</span>
                    </td>
                    <td className="px-6 py-3.5 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#1a3884]/10 text-[#1a3884] dark:bg-[#1a3884]/30 dark:text-white flex items-center justify-center font-bold text-xs shadow-sm shrink-0 uppercase">
                          {item.logo ? <img src={item.logo} alt="" className="w-full h-full rounded-full object-cover" /> : item.collegeName.slice(0, 2)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 dark:text-white text-sm leading-tight truncate">{item.collegeName}</p>
                          <p className="text-xs text-slate-550 dark:text-gray-400 mt-1">Adoption Rate: {item.adoptionRate}%</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 align-middle">
                      <div className="grid grid-cols-[minmax(80px,1fr)_36px] items-center gap-2">
                        <div className="h-2 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[var(--placement-primary)] dark:bg-[#4f7dff] rounded-full transition-all duration-500" 
                            style={{ width: `${item.adoptionScore || 0}%` }}
                          />
                        </div>
                        <span className="w-8 text-xs font-bold text-[var(--placement-primary)] dark:text-[#4f7dff] tabular-nums">
                          {item.adoptionScore || 0}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 align-middle font-semibold text-slate-900 dark:text-white text-center tabular-nums">
                      {item.totalStudents}
                    </td>
                    <td className="px-6 py-3.5 align-middle text-center">
                      <span className="text-xs font-semibold px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        {item.avgCourseProgress || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-3.5 align-middle text-center">
                      <span className="text-xs font-semibold px-2 py-1 rounded-md bg-blue-50 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff]">
                        {item.avgEngagement || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-3.5 align-middle text-center">
                      <span className="text-xs font-semibold px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        {item.avgReadiness || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-3.5 align-middle text-center">
                      <div className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 dark:text-slate-300">
                        <span className="text-slate-900 dark:text-white">{item.totalPlacements}</span> Placed
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6 text-[14px]">
      {/* Header Section */}
      <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/college/analytics')}
            className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors"
            title="Back to Performance Analytics"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-0.5">
            <h1 className="text-[22px] font-black text-[var(--placement-primary)] tracking-tight">
              Student Progress Tracking
            </h1>
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
              <TrendingUp className="w-4 h-4 text-[var(--placement-primary)] opacity-60" />
              Monitor and track student placement readiness
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* View Toggle */}
          {user?.role === 'admin' && (
            <div className="flex bg-slate-100/80 dark:bg-[#1a3884]/20 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('student')}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${viewMode === 'student' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}
              >
                <User className="w-3.5 h-3.5" />
                Student View
              </button>
              <button
                onClick={() => setViewMode('college')}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${viewMode === 'college' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}
              >
                <Award className="w-3.5 h-3.5" />
                Leaderboard
              </button>
            </div>
          )}

          <button 
            onClick={() => {
              if (viewMode === 'student') { fetchStudents(); fetchAnalytics(); } else { fetchLeaderboard(); }
            }}
            className="border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#001f40] rounded-xl px-4 py-2.5 text-sm font-bold flex items-center gap-2 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button 
            className="border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#001f40] rounded-xl px-4 py-2.5 text-sm font-bold flex items-center gap-2 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Analytics Overview */}
      {viewMode === 'student' ? (
        <>
          {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <SummaryCard
            title="Total Institutes"
            value={colleges?.length || 0}
            icon={GraduationCap}
            loading={loading}
            layout="row"
          />
          <SummaryCard
            title="Total Students"
            value={analytics.department.reduce((sum, d) => sum + d.totalStudents, 0)}
            icon={Users}
            loading={loading}
            layout="row"
          />
          <SummaryCard
            title="Active Batches"
            value={analytics.batch.length}
            icon={Calendar}
            loading={loading}
            layout="row"
          />
          <SummaryCard
            title="Departments"
            value={analytics.department.length}
            icon={BookOpen}
            loading={loading}
            layout="row"
          />
        </div>
      )}



      {/* Search and Filters Card */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-[#1a3884]/20 space-y-4">
        {/* Search Input */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-grow w-full relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search students by name, roll number, or email..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Cascading Filter Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <div>
              <select
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px]"
                value={filters.institute}
                onChange={(e) => {
                  setFilters({ ...filters, institute: e.target.value, department: '', batch: '' });
                  setPage(1);
                }}
              >
                <option value="">All Institutes</option>
                {colleges.map((col) => (
                  <option key={col._id} value={col._id}>{col.collegeName}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <select
              className={`w-full px-3 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px] ${((user?.role === 'admin' || user?.role === 'superadmin') && !filters.institute) ? 'opacity-50 cursor-not-allowed' : ''}`}
              value={filters.department}
              onChange={(e) => {
                setFilters({ ...filters, department: e.target.value });
                setPage(1);
              }}
              disabled={(user?.role === 'admin' || user?.role === 'superadmin') && !filters.institute}
            >
              <option value="">All Departments</option>
              {analytics?.department.map((dept) => (
                <option key={dept.department} value={dept.department}>{dept.department}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className={`w-full px-3 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px] ${((user?.role === 'admin' || user?.role === 'superadmin') && !filters.institute) ? 'opacity-50 cursor-not-allowed' : ''}`}
              value={filters.batch}
              onChange={(e) => {
                setFilters({ ...filters, batch: e.target.value });
                setPage(1);
              }}
              disabled={(user?.role === 'admin' || user?.role === 'superadmin') && !filters.institute}
            >
              <option value="">All Batches</option>
              {analytics?.batch.map((batch) => (
                <option key={batch.batch} value={batch.batch}>{batch.batch}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="w-full px-3 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white transition-all h-[42px]"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="graduated">Graduated</option>
            </select>
          </div>
        </div>
      </div>

      {/* Student List */}
      <Card className="shadow-sm overflow-hidden bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-xl [&>div]:p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] table-fixed text-sm">
            <colgroup>
              <col className="w-[27%]" />
              <col className="w-[14%]" />
              <col className="w-[17%]" />
              <col className="w-[17%]" />
              <col className="w-[13%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="bg-slate-50 dark:bg-[#001f40]/50 border-b border-gray-200 dark:border-[#1a3884]/30">
              <tr>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button 
                    onClick={() => handleSort('fullName')}
                    className="flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-[var(--placement-primary)] dark:hover:text-[#4f7dff] transition-colors"
                  >
                    Student
                    {sortBy === 'fullName' && <ArrowUpDown className="w-3 h-3" />}
                  </button>
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Institute
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button 
                    onClick={() => handleSort('engagementRate')}
                    className="flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-[var(--placement-primary)] dark:hover:text-[#4f7dff] transition-colors"
                  >
                    Engagement Rate
                    {sortBy === 'engagementRate' && <ArrowUpDown className="w-3 h-3" />}
                  </button>
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button 
                    onClick={() => handleSort('courseProgress')}
                    className="flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-[var(--placement-primary)] dark:hover:text-[#4f7dff] transition-colors"
                  >
                    Course Progress
                    {sortBy === 'courseProgress' && <ArrowUpDown className="w-3 h-3" />}
                  </button>
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Engagement Signal
                </th>
                <th className="px-6 py-3.5 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-[#1a3884]/20">
              {students.map((student) => (
                <tr
                  key={student._id}
                  className="hover:bg-slate-50/50 dark:hover:bg-[#1a3884]/10 transition-colors cursor-pointer"
                  onClick={() => handleStudentClick(student)}
                >
                  <td className="px-6 py-3.5 align-middle">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-full flex items-center justify-center text-white font-bold shadow-sm overflow-hidden shrink-0">
                        {student.fullName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm leading-tight truncate">{student.fullName || 'N/A'}</p>
                        <p className="text-xs text-gray-550 dark:text-gray-400 mt-1">{student.studentId || student.rollNumber || 'No ID'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 align-middle">
                    <span className="block text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {student.college?.collegeName || student.college?.name || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 align-middle">
                    <div className="grid grid-cols-[minmax(88px,1fr)_42px] items-center gap-3">
                      <div className="h-2 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[var(--placement-primary)] dark:bg-[#4f7dff] rounded-full transition-all duration-500" 
                          style={{ width: `${student.engagementRate || 0}%` }}
                        />
                      </div>
                      <span className="w-9 text-xs font-semibold text-[var(--placement-primary)] dark:text-[#4f7dff] tabular-nums">
                        {student.engagementRate || 0}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 align-middle">
                    <div className="grid grid-cols-[minmax(88px,1fr)_42px] items-center gap-3">
                      <div className="h-2 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${getReadinessColor(student.courseProgress || 0)}`} 
                          style={{ width: `${student.courseProgress || 0}%` }}
                        />
                      </div>
                      <span className="w-9 text-xs font-semibold text-slate-600 dark:text-slate-300 tabular-nums">
                        {student.courseProgress || 0}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 align-middle">
                    {(() => {
                      const rate = student.engagementRate || 0;
                      let risk = { label: 'High Risk', color: 'bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-500/20 dark:border-rose-500/30 dark:text-rose-400', dot: 'bg-rose-500 dark:bg-rose-400' };
                      if (rate >= 70) risk = { label: 'Safe', color: 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-500/20 dark:border-emerald-500/30 dark:text-emerald-400', dot: 'bg-emerald-500 dark:bg-emerald-400' };
                      else if (rate >= 40) risk = { label: 'At Risk', color: 'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-500/20 dark:border-amber-500/30 dark:text-amber-400', dot: 'bg-amber-500 dark:bg-amber-400' };
                      
                      return (
                        <div className={`inline-flex items-center justify-center min-w-[82px] gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${risk.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${risk.dot} animate-pulse shrink-0`} />
                          {risk.label}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-3.5 align-middle">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCalculateReadiness(student._id); }}
                        title="Recalculate readiness"
                        className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 dark:text-gray-400 transition-colors"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStudentClick(student); }}
                        className="h-8 min-w-[66px] inline-flex items-center justify-center px-3 text-xs font-bold bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] active:scale-95 text-white rounded-lg transition-all shadow-sm"
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-[#1a3884]/20 bg-slate-50/50 dark:bg-[#001f40]/20">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-gray-200 dark:border-[#1a3884]/30 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-600 dark:text-gray-300 transition-all disabled:opacity-40 bg-white dark:bg-[#001f40]"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-gray-200 dark:border-[#1a3884]/30 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-600 dark:text-gray-300 transition-all disabled:opacity-40 bg-white dark:bg-[#001f40]"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
        </>
      ) : (
        renderLeaderboard()
      )}





      {/* Student Detail View Modal */}
      <AnimatePresence>
        {showDetailView && selectedStudent && (
          ['admin', 'superadmin'].includes(user?.role) ? (
            <SuperAdminQuickLook
              student={selectedStudent}
              onClose={() => {
                setShowDetailView(false);
                setSelectedStudent(null);
              }}
            />
          ) : (









            <StudentDetailView
              student={selectedStudent}
              onClose={() => {
                setShowDetailView(false);
                setSelectedStudent(null);
              }}
              onUpdate={fetchStudents}
            />
          )

        )}
      </AnimatePresence>
    </div>
  );
};

// Student Detail View Component
const StudentDetailView = ({ student, onClose, onUpdate }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [notes, setNotes] = useState('');
  const [suitableJobs, setSuitableJobs] = useState([]);
  const [suggestedPath, setSuggestedPath] = useState('');
  const [savingPath, setSavingPath] = useState(false);

  useEffect(() => {
    fetchStudentDetails();
    fetchSuitableJobs();
  }, [student._id]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/student-progress/students/${student._id}`);
      if (response.data.success) {
        setStudentData(response.data.data);
        setSuggestedPath(response.data.data.placementProfile?.suggestedCareerPathByOfficer || '');
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      toast.error('Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuggestedPath = async () => {
    if (!suggestedPath) {
      toast.error('Please select a target career path first');
      return;
    }
    try {
      setSavingPath(true);
      const response = await api.put(`/student-progress/students/${student._id}/profile`, {
        suggestedCareerPathByOfficer: suggestedPath
      });
      if (response.data.success) {
        toast.success('Career path suggestion updated successfully');
        setStudentData(prev => ({
          ...prev,
          placementProfile: {
            ...prev.placementProfile,
            suggestedCareerPathByOfficer: suggestedPath
          }
        }));
        // Refetch recommendations since path changed
        fetchSuitableJobs();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error('Failed to save career path suggestion');
    } finally {
      setSavingPath(false);
    }
  };

  const fetchSuitableJobs = async () => {
    try {
      const response = await api.get(`/student-progress/students/${student._id}/suitable-jobs`);
      if (response.data.success) {
        setSuitableJobs(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching suitable jobs:', error);
    }
  };

  const handleInviteToDrive = (companyName, jobTitle) => {
    toast.success(`Invite to Apply sent for "${companyName} - ${jobTitle}"`);
    api.post(`/student-progress/students/${student._id}/communications`, {
      type: 'email',
      subject: `Placement Drive Invite: ${companyName}`,
      notes: `Placement Officer recommended candidate to apply for ${jobTitle} at ${companyName}.`
    }).catch(err => console.error('Failed to log invite communication:', err));
  };

  const handleRecommendCourse = (skillName) => {
    toast.success(`Recommended upskilling training for skill gap: "${skillName}"`);
    api.post(`/student-progress/students/${student._id}/communications`, {
      type: 'email',
      subject: `Upskilling Recommendation: ${skillName}`,
      notes: `Placement Officer recommended student to enroll in training courses to bridge gap in: ${skillName}.`
    }).catch(err => console.error('Failed to log upskilling recommendation:', err));
  };

  const handleAddNote = async () => {
    if (!notes.trim()) return;
    try {
      await api.post(`/student-progress/students/${student._id}/notes`, { note: notes });
      toast.success('Note added successfully');
      setNotes('');
      fetchStudentDetails();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#002147] rounded-2xl p-8 border border-gray-100 dark:border-[#1a3884]/20 shadow-2xl">
          <div className="w-12 h-12 border-4 border-blue-100 border-t-[#1a3884] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const detailTabs = [
    { id: 'overview', name: 'Overview', icon: User },
    { id: 'academic', name: 'Academic', icon: BookOpen },
    { id: 'placements', name: 'Placements', icon: Briefcase },
    { id: 'skills', name: 'Skills', icon: Award },
    { id: 'matching', name: 'Job Match', icon: Target },
    { id: 'timeline', name: 'Timeline', icon: Calendar },
  ];

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-[#002147] rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col border border-gray-100 dark:border-[#1a3884]/20"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-[#00152E] to-[#1a3884] flex-shrink-0 overflow-hidden shadow-md">
          <div className="absolute inset-0 bg-white/5 pointer-events-none backdrop-blur-[2px]" />
          <div className="relative px-7 py-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center font-black text-2xl text-[#1a3884] shadow-md flex-shrink-0 border-[3px] border-white/20">
                {studentData?.student?.fullName?.charAt(0) || '?'}
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-white tracking-tight truncate">{studentData?.student?.fullName || 'N/A'}</h3>
                <p className="text-white/80 text-xs font-mono mt-0.5 tracking-wider">{studentData?.student?.studentId || studentData?.student?.rollNumber || '—'}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {studentData?.student?.department && (
                    <span className="flex items-center gap-1 text-[11px] bg-white/20 border border-white/30 text-white px-2.5 py-0.5 rounded-full font-semibold whitespace-nowrap shadow-sm backdrop-blur-sm">
                      <GraduationCap className="w-3 h-3 flex-shrink-0" />
                      {studentData.student.department?.fullName || studentData.student.department?.abbreviation || studentData.student.department}
                    </span>
                  )}
                  {studentData?.student?.batch && (
                    <span className="flex items-center gap-1 text-[11px] bg-white/20 border border-white/30 text-white px-2.5 py-0.5 rounded-full font-semibold whitespace-nowrap shadow-sm backdrop-blur-sm">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      Batch {studentData.student.batch}
                    </span>
                  )}
                  {studentData?.student?.semester && (
                    <span className="text-[11px] bg-white/20 border border-white/30 text-white px-2.5 py-0.5 rounded-full font-semibold whitespace-nowrap shadow-sm backdrop-blur-sm">
                      Sem {studentData.student.semester}
                    </span>
                  )}
                  <span className={`flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full font-bold border whitespace-nowrap shadow-sm backdrop-blur-sm ${(studentData?.placementProfile?.readinessScore || 0) >= 70
                      ? 'bg-emerald-500/80 border-emerald-400 text-white'
                      : (studentData?.placementProfile?.readinessScore || 0) >= 40
                        ? 'bg-amber-500/80 border-amber-400 text-white'
                        : 'bg-rose-500/80 border-rose-400 text-white'
                    }`}>
                    <Zap className="w-3 h-3 flex-shrink-0" />
                    {studentData?.placementProfile?.readinessScore || 0}% Ready
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">

              <button
                onClick={onClose}
                className="p-2 ml-2 hover:bg-white/10 rounded-xl transition-colors text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-[#1a3884]/20 overflow-x-auto scrollbar-hide flex-nowrap flex-shrink-0 bg-white dark:bg-[#002147]">
          {detailTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all flex-shrink-0 ${activeTab === tab.id
                  ? 'text-[#1a3884] dark:text-[#4f7dff] border-b-[3px] border-[#1a3884] dark:border-[#4f7dff] bg-blue-50/80 dark:bg-[#1a3884]/15'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 border-b-[3px] border-transparent'
                }`}
            >
              <tab.icon className="w-[18px] h-[18px]" />
              {tab.name}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar bg-white dark:bg-[#002147]">
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Career Path Alignment */}
              <Card className="border-none shadow-lg p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="space-y-2 flex-1 text-left">
                    <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                      Career Agent Path Status
                    </h4>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      {studentData?.placementProfile?.lockedCareerPaths && studentData.placementProfile.lockedCareerPaths.length > 0 ? (
                        studentData.placementProfile.lockedCareerPaths.map((path, idx) => (
                          <div key={idx} className="bg-blue-50 dark:bg-[#1a3884]/20 border border-blue-100 dark:border-[#1a3884]/30 text-blue-800 dark:text-blue-300 rounded-xl px-4 py-3 flex items-center gap-3">
                            <div className="w-2.5 h-2.5 rounded-full bg-[#1a3884] dark:bg-[#4f7dff] animate-pulse" />
                            <div>
                              <span className="text-[10px] text-blue-500 dark:text-[#4f7dff] uppercase tracking-wider block font-bold">Student Locked Path {idx + 1}</span>
                              <span className="font-bold text-sm text-gray-800 dark:text-gray-205">{path}</span>
                            </div>
                          </div>
                        ))
                      ) : studentData?.placementProfile?.lockedCareerPath ? (
                        <div className="bg-blue-50 dark:bg-[#1a3884]/20 border border-blue-100 dark:border-[#1a3884]/30 text-blue-800 dark:text-blue-300 rounded-xl px-4 py-3 flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#1a3884] dark:bg-[#4f7dff] animate-pulse" />
                          <div>
                            <span className="text-[10px] text-blue-500 dark:text-[#4f7dff] uppercase tracking-wider block font-bold">Student Locked Path</span>
                            <span className="font-bold text-sm text-gray-800 dark:text-gray-205">{studentData.placementProfile.lockedCareerPath}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 text-amber-800 dark:text-amber-300 rounded-xl px-4 py-3 flex items-center gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          <div>
                            <span className="text-[10px] text-amber-600 dark:text-amber-400 uppercase tracking-wider block font-bold">Career Agent Status</span>
                            <span className="font-semibold text-xs text-gray-800 dark:text-gray-205">No path locked by student yet.</span>
                          </div>
                        </div>
                      )}

                      {studentData?.placementProfile?.suggestedCareerPathByOfficer && (
                        <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-xl px-4 py-3 flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          <div>
                            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block font-bold">Officer Recommended Path</span>
                            <span className="font-bold text-sm text-gray-800 dark:text-gray-205">{studentData.placementProfile.suggestedCareerPathByOfficer}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </Card>

              <div className="bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-2xl p-5 shadow-sm text-left">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">📬 Contact Information</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { icon: Mail, label: 'Email', value: studentData?.student?.email, color: 'text-[#1a3884] dark:text-[#4f7dff]', bg: 'bg-blue-50 dark:bg-[#1a3884]/20' },
                    { icon: Phone, label: 'Mobile', value: studentData?.student?.mobile, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/20' },
                    { icon: MapPin, label: 'Semester', value: studentData?.student?.semester, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/20' },
                  ].map(({ icon: Icon, label, value, color, bg }) => (
                    <div key={label} className={`flex items-center gap-3 p-3 ${bg} rounded-xl`}>
                      <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                      <div>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wider">{label}</p>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-250 truncate">{value || 'N/A'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>



              <div className="bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-2xl p-5 shadow-sm text-left">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">📝 Placement Officer Notes</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a note about this student..."
                    className="flex-1 px-4 py-2.5 text-sm bg-white dark:bg-[#001f40] text-gray-900 dark:text-white border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-[#1a3884] outline-none transition-all"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  <button onClick={handleAddNote} className="px-4 py-2.5 bg-[#1a3884] hover:bg-[#132A55] text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap">
                    Add Note
                  </button>
                </div>
                {studentData?.placementProfile?.placementOfficerNotes?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {studentData.placementProfile.placementOfficerNotes.map((note, index) => (
                      <div key={index} className="bg-blue-50 dark:bg-[#1a3884]/20 border border-blue-100 dark:border-[#1a3884]/30 px-4 py-3 rounded-xl">
                        <p className="text-sm text-gray-700 dark:text-gray-300">{note.note}</p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{new Date(note.addedAt).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'academic' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Batch', value: studentData?.student?.batch || 'N/A', icon: Users, gradient: 'from-[#00152E] to-[#1a3884]', shadow: 'shadow-[#1a3884]/20', color: 'text-[#1a3884] dark:text-[#4f7dff]' },
                  { label: 'Current CGPA', value: studentData?.placementProfile?.currentCGPA?.toFixed(2) || '0.00', icon: BookOpen, gradient: 'from-[#00152E] to-[#1a3884]', shadow: 'shadow-[#1a3884]/20', color: 'text-[#1a3884] dark:text-[#4f7dff]' },
                ].map(({ label, value, icon: Icon, gradient, shadow, color }) => (
                  <div key={label} className="bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-xl p-3.5 shadow-sm flex items-center gap-3 text-left">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} shadow-sm ${shadow} flex items-center justify-center text-white flex-shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-400 uppercase tracking-widest">{label}</p>
                      <p className={`text-xl font-black ${color} leading-tight`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50/50 dark:bg-[#1a3884]/10 border border-blue-100 dark:border-[#1a3884]/20 rounded-xl px-4 py-3 flex items-center gap-2 text-left mt-2">
                <CheckCircle2 className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                <span className="text-xs text-blue-800 dark:text-blue-300 font-medium">
                  Academic data is automatically synced from student registrations.
                </span>
              </div>

              <div className="bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-2xl p-5 shadow-sm text-left">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Assessment Scores</p>
                {studentData?.assessmentData?.length > 0 ? (
                  <div className="space-y-3">
                    {studentData.assessmentData.map((assessment, index) => {
                      const score = getAssessmentScore(assessment) || 0;
                      return (
                      <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-[#001f40] px-4 py-3 rounded-xl border border-slate-100 dark:border-[#1a3884]/10">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate pr-3">{getAssessmentName(assessment)}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                            <div className="h-full bg-[#1a3884] dark:bg-[#4f7dff] rounded-full" style={{ width: `${score}%` }} />
                          </div>
                          <span className="text-sm font-black text-[#1a3884] dark:text-[#4f7dff] w-10 text-right">{score}%</span>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">📋 No assessment data available</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'placements' && (
            <div className="space-y-6 text-left">
              <Card className="border-none shadow-lg p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <h4 className="font-bold text-gray-900 dark:text-white mb-4">Placement Applications</h4>
                {studentData?.applications?.length > 0 ? (
                  <div className="space-y-4">
                    {studentData.applications.map((app, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-[#001f40] border border-gray-100 dark:border-[#1a3884]/10 p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-gray-900 dark:text-gray-200">{app.jobPosting?.title || 'N/A'}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{app.jobPosting?.company?.name || 'N/A'}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${app.status === 'placed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' :
                              app.status === 'offered' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400' :
                                app.status === 'interviewing' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400' :
                                  (app.status === 'applied' || app.status === 'shortlisted') ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400' :
                                    'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-gray-400'
                            }`}>
                            {app.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No placement applications yet</p>
                )}
              </Card>
            </div>
          )}

          {activeTab === 'matching' && (
            <div className="space-y-6 animate-fade-in text-left">
              {/* Target Path Alignment Header Card */}
              <div className="bg-white dark:bg-[#002147] border border-blue-100 dark:border-[#1a3884]/20 p-5 rounded-2xl shadow-sm flex flex-col xl:flex-row xl:items-center justify-between gap-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 dark:bg-blue-950/10 rounded-full blur-3xl opacity-60 translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                <div className="space-y-1.5 flex-1 relative z-10">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#1a3884] dark:text-[#4f7dff]">Target Drive Alignment</span>
                  </div>
                  <h4 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Locked Career Agent Path</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 max-w-lg leading-relaxed">
                    Campus placement drives are prioritized and matching suitability is calculated using the student's locked-in career agent path and verified skills.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 relative z-10 w-full xl:w-auto">
                  <div className="bg-blue-50/80 dark:bg-[#1a3884]/20 border border-blue-100/50 dark:border-[#1a3884]/30 rounded-xl px-4 py-3 flex-1 xl:max-w-[350px]">
                    <span className="text-[9px] text-[#4f7dff] block font-bold uppercase tracking-wider mb-1">Locked Paths</span>
                    <span className="font-bold text-sm text-[#1a3884] dark:text-blue-200 leading-snug block">
                      {studentData?.placementProfile?.lockedCareerPaths?.join(' | ') || studentData?.placementProfile?.lockedCareerPath || 'Not locked yet'}
                    </span>
                  </div>
                  <div className="bg-emerald-50/80 dark:bg-emerald-950/20 border border-emerald-100/50 dark:border-emerald-900/30 rounded-xl px-4 py-3 min-w-[160px] max-w-full xl:max-w-[200px]">
                    <span className="text-[9px] text-emerald-500 dark:text-emerald-400 block font-bold uppercase tracking-wider mb-1">Officer Suggestion</span>
                    <span className="font-bold text-sm text-emerald-900 dark:text-emerald-250 leading-snug block">
                      {studentData?.placementProfile?.suggestedCareerPathByOfficer || 'No suggestion yet'}
                    </span>
                  </div>
                </div>
              </div>
              <Card className="border-none shadow-lg p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-bold text-gray-900 dark:text-white text-lg">Smart Job & Company Recommendations</h4>
                  <span className="text-xs bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-305 px-3 py-1 rounded-full font-bold">
                    {suitableJobs.length} Matched Drives
                  </span>
                </div>

                {suitableJobs.length > 0 ? (
                  <div className="space-y-6">
                    {suitableJobs.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white dark:bg-[#002147] hover:bg-slate-50/50 dark:hover:bg-[#1a3884]/5 p-6 rounded-2xl border border-slate-100 dark:border-[#1a3884]/20 hover:border-blue-100 dark:hover:border-[#4f7dff]/30 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col gap-5"
                      >
                        {/* Company & Drive Header Info */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center text-white font-bold text-lg shadow-sm">
                              {item.job.company?.name?.charAt(0) || 'C'}
                            </div>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <h5 className="font-black text-gray-900 dark:text-white text-lg">{item.job.company?.name}</h5>
                                <span className="text-xs text-gray-400 font-medium">|</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider">{item.job.location}</span>
                              </div>
                              <p className="text-sm font-bold text-[#1a3884] dark:text-[#4f7dff] mt-0.5">{item.job.title}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 self-end md:self-auto">
                            <div className="text-right">
                              <span className="text-xs text-gray-400 dark:text-gray-500 block font-semibold">Suitability Score</span>
                              <span className="text-2xl font-black text-slate-800 dark:text-gray-200">{item.suitabilityScore}%</span>
                            </div>
                            <div className="w-16 h-16 relative flex items-center justify-center">
                              <svg className="w-14 h-14 transform -rotate-90">
                                <circle
                                  cx="28" cy="28" r="24" stroke="#f1f5f9" strokeWidth="4" fill="transparent" className="stroke-slate-100 dark:stroke-slate-800"
                                />
                                <circle
                                  cx="28" cy="28" r="24" stroke={item.suitabilityScore >= 75 ? '#10b981' : item.suitabilityScore >= 50 ? '#f59e0b' : '#ef4444'}
                                  strokeWidth="4" fill="transparent"
                                  strokeDasharray={150.79}
                                  strokeDashoffset={150.79 - (150.79 * item.suitabilityScore) / 100}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span className="absolute text-[10px] font-black text-slate-700 dark:text-gray-300">MATCH</span>
                            </div>
                          </div>
                        </div>

                        {/* Match Breakdown & Progress Bars */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 dark:bg-[#001f40] p-4 rounded-xl border border-slate-100 dark:border-[#1a3884]/10">
                          <div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold block mb-1">Path Alignment</span>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-[#1a3884] dark:bg-[#4f7dff]"
                                  style={{ width: `${item.matchBreakdown?.pathAlignment || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold text-slate-700 dark:text-gray-300">{item.matchBreakdown?.pathAlignment || 0}%</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold block mb-1">Verified Skill Match</span>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500"
                                  style={{ width: `${item.matchBreakdown?.skillMatch || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold text-slate-700 dark:text-gray-300">{item.matchBreakdown?.skillMatch || 0}%</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold block mb-1">SMAART Readiness Index</span>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-amber-500"
                                  style={{ width: `${item.matchBreakdown?.readiness || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold text-slate-700 dark:text-gray-300">{item.matchBreakdown?.readiness || 0}%</span>
                            </div>
                          </div>
                        </div>

                        {/* Skills Analysis Section */}
                        <div className="space-y-3">
                          {item.matchingSkills?.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mr-2">Verified Skills:</span>
                              {item.matchingSkills.map((skill, sIdx) => (
                                <span key={sIdx} className="bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/30 rounded-lg px-2.5 py-1 text-xs font-medium flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}

                          {item.missingSkills?.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 dark:border-[#1a3884]/20 pt-3">
                              <span className="text-xs text-rose-500 dark:text-rose-405 font-bold uppercase tracking-wider mr-2 flex items-center gap-1">
                                <AlertCircle className="w-3.5 h-3.5" />
                                Skill Gaps:
                              </span>
                              {item.missingSkills.map((skill, sIdx) => (
                                <div key={sIdx} className="flex items-center gap-1.5 bg-rose-50/50 dark:bg-rose-950/10 hover:bg-rose-50 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/30 rounded-lg pl-2.5 pr-1.5 py-1 text-xs font-medium transition-colors">
                                  {skill}
                                  <button
                                    onClick={() => handleRecommendCourse(skill)}
                                    title="Recommend learning course to bridge gap"
                                    className="p-0.5 hover:bg-rose-100 dark:hover:bg-rose-950 rounded-md text-rose-600 dark:text-rose-455 transition-colors"
                                  >
                                    <BookOpen className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="bg-emerald-50/40 dark:bg-emerald-950/10 border border-emerald-100/50 dark:border-emerald-900/30 rounded-xl p-3 text-xs text-emerald-800 dark:text-emerald-350 font-semibold flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-450" />
                              Perfect Skill Match! The candidate possesses all skills required for this job drive.
                            </div>
                          )}
                        </div>

                        {/* Interactive Drive CTA Actions */}
                        <div className="flex justify-between items-center border-t border-slate-100 dark:border-[#1a3884]/20 pt-4 mt-1">
                          <span className="text-sm text-[#1a3884] dark:text-[#4f7dff] font-black">
                            Package: {(() => {
                              const pkg = item.job.salaryPackage;
                              if (!pkg) return 'N/A';
                              const s = pkg.toString();
                              return /lpa|month|year|unpaid/i.test(s) ? s : `${s} LPA`;
                            })()}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleInviteToDrive(item.job.company?.name, item.job.title)}
                              className="border-gray-200 dark:border-[#1a3884]/30 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1a3884]/15 font-bold rounded-xl text-xs px-4 py-2"
                            >
                              Invite to Apply
                            </Button>
                            <button
                              onClick={() => {
                                onClose();
                                setSearchParams({ jobId: item.job._id });
                              }}
                              className="bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl shadow-md hover:shadow-lg active:scale-95 hover:scale-[1.03] font-bold text-xs px-4 py-2 transition-all duration-200"
                            >
                              View Drive
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 space-y-3">
                    <p className="text-gray-400 font-medium">No suitable job recommendations found matching the student's profile</p>
                    <p className="text-xs text-gray-400">
                      Try updating the student's target career path suggestion or verify new skills in the Skills tab.
                    </p>
                  </div>
                )}
              </Card>
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="space-y-6 text-left">
              <Card className="border-none shadow-sm p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                    Verified Skills
                  </h4>
                </div>
                {studentData?.placementProfile?.skills?.length > 0 ? (
                  <div className="flex flex-wrap gap-2.5">
                    {studentData.placementProfile.skills.map((skill, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-[#1a3884]/20 dark:to-indigo-900/20 text-[#1a3884] dark:text-[#4f7dff] rounded-xl border border-blue-100 dark:border-[#1a3884]/30 shadow-sm"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-[#4f7dff]" />
                        <span className="text-sm font-semibold">{skill.name}</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-white/60 dark:bg-black/20 px-2 py-0.5 rounded-md ml-1 opacity-80">
                          {skill.level}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-slate-50/50 dark:bg-[#1a3884]/5 rounded-xl border border-dashed border-slate-200 dark:border-[#1a3884]/20">
                    <Award className="w-10 h-10 text-slate-300 dark:text-[#1a3884]/40 mb-3" />
                    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No skills verified yet</p>
                    <p className="text-xs text-slate-400 mt-1">Skills are automatically synced from course completions</p>
                  </div>
                )}
              </Card>

              <Card className="border-none shadow-sm p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
                    Certificates
                  </h4>
                </div>
                {studentData?.placementProfile?.certificates?.length > 0 ? (
                  <div className="space-y-3">
                    {studentData.placementProfile.certificates.map((cert, index) => (
                      <div key={index} className="bg-slate-50 dark:bg-[#001f40] p-4 rounded-xl flex justify-between items-start border border-slate-100 dark:border-[#1a3884]/20 hover:border-emerald-200 dark:hover:border-emerald-900/50 transition-colors shadow-sm">
                        <div>
                          <p className="font-bold text-gray-900 dark:text-gray-100">{cert.title || cert.name}</p>
                          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mt-0.5 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {cert.issuingOrg || cert.issuer || 'N/A'}
                          </p>
                          {cert.issueDate && (
                            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mt-2 uppercase tracking-wider">
                              Issued: {new Date(cert.issueDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        {cert.certificateFile && (
                          <a href={cert.certificateFile} target="_blank" rel="noopener noreferrer" className="text-emerald-700 dark:text-emerald-300 hover:text-white dark:hover:text-white text-sm font-bold bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-600 dark:hover:bg-emerald-600 px-4 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800/50 transition-all">
                            View Cert
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-slate-50/50 dark:bg-[#1a3884]/5 rounded-xl border border-dashed border-slate-200 dark:border-[#1a3884]/20">
                    <Award className="w-10 h-10 text-slate-300 dark:text-[#1a3884]/40 mb-3" />
                    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No certificates uploaded</p>
                  </div>
                )}
              </Card>

              <Card className="border-none shadow-sm p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                    Projects
                  </h4>
                </div>
                {studentData?.placementProfile?.projects?.length > 0 ? (
                  <div className="space-y-3">
                    {studentData.placementProfile.projects.map((proj, index) => (
                      <div key={index} className="bg-slate-50 dark:bg-[#001f40] p-4 rounded-xl flex flex-col gap-2 border border-slate-100 dark:border-[#1a3884]/20 shadow-sm">
                        <div className="flex justify-between items-start">
                          <p className="font-bold text-gray-900 dark:text-gray-100 text-base">{proj.title}</p>
                          {proj.projectUrl && (
                            <a href={proj.projectUrl} target="_blank" rel="noopener noreferrer" className="text-[#1a3884] dark:text-[#4f7dff] hover:bg-[#1a3884] hover:text-white dark:hover:bg-[#4f7dff] dark:hover:text-white text-xs font-bold bg-blue-50 dark:bg-[#1a3884]/30 px-3 py-1.5 rounded-lg border border-blue-200/50 transition-all flex items-center gap-1">
                              Visit <ChevronRight className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">{proj.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-slate-50/50 dark:bg-[#1a3884]/5 rounded-xl border border-dashed border-slate-200 dark:border-[#1a3884]/20">
                    <Briefcase className="w-10 h-10 text-slate-300 dark:text-[#1a3884]/40 mb-3" />
                    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No projects added</p>
                  </div>
                )}
              </Card>

              <Card className="border-none shadow-sm p-6 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="font-bold text-gray-900 dark:text-white text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
                    Training Programs
                  </h4>
                </div>
                {studentData?.placementProfile?.trainingPrograms?.length > 0 ? (
                  <div className="space-y-3">
                    {studentData.placementProfile.trainingPrograms.map((training, index) => (
                      <div key={index} className="bg-slate-50 dark:bg-[#001f40] p-4 rounded-xl border border-slate-100 dark:border-[#1a3884]/20 shadow-sm hover:shadow transition-all">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-gray-900 dark:text-gray-100">{training.programName}</p>
                            <span className={`inline-block mt-2 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-wider font-bold ${training.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-450 border border-emerald-200 dark:border-emerald-800' :
                                training.status === 'in-progress' ? 'bg-blue-100 text-[#1a3884] dark:bg-blue-950/40 dark:text-[#4f7dff] border border-blue-200 dark:border-[#1a3884]' :
                                  'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                              }`}>
                              {training.status}
                            </span>
                          </div>
                          <div className="text-right flex flex-col items-end">
                            <p className="text-sm font-black text-[#1a3884] dark:text-[#4f7dff]">{training.progress}%</p>
                            <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden mt-1.5 shadow-inner">
                              <div
                                className="h-full bg-gradient-to-r from-[#00152E] to-[#1a3884]"
                                style={{ width: `${training.progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-slate-50/50 dark:bg-[#1a3884]/5 rounded-xl border border-dashed border-slate-200 dark:border-[#1a3884]/20">
                    <BookOpen className="w-10 h-10 text-slate-300 dark:text-[#1a3884]/40 mb-3" />
                    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No training programs enrolled</p>
                  </div>
                )}
              </Card>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-6 text-left">
              <div className="bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/20 rounded-2xl p-6 shadow-sm">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6">⏳ Placement Journey Timeline</p>
                <div className="relative border-l-2 border-blue-100 dark:border-blue-900/30 ml-4 space-y-8 pb-4">
                  {/* Initial Node */}
                  <div className="relative pl-8">
                    <div className="absolute -left-3.5 top-0 w-7 h-7 bg-blue-100 dark:bg-[#002147] rounded-full flex items-center justify-center border-4 border-white dark:border-[#002147] shadow-sm">
                      <User className="w-3 h-3 text-[#1a3884] dark:text-[#4f7dff]" />
                    </div>
                    <div className="bg-gray-50 dark:bg-[#001f40] border border-gray-100 dark:border-[#1a3884]/15 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <h5 className="font-bold text-gray-900 dark:text-gray-205 text-sm">Student Profile Created</h5>
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 bg-white dark:bg-[#002147] px-2 py-1 rounded-full shadow-sm border border-gray-100 dark:border-[#1a3884]/20">
                          {new Date(studentData?.student?.admissionDate || Date.now()).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-450 mt-1">Student successfully onboarded to the SMAART ecosystem.</p>
                    </div>
                  </div>

                  {/* Application Nodes */}
                  {studentData?.applications?.map((app, index) => {
                    const isPlaced = app.status === 'placed';
                    const isInterviewing = app.status === 'interviewing';
                    const isOffered = app.status === 'offered';

                    let bgIcon = 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-450';
                    let Icon = Briefcase;
                    let badgeClass = 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-800 dark:text-gray-400 dark:border-[#1a3884]/20';
                    let badgeText = app.status || 'Applied';

                    if (isPlaced) {
                      bgIcon = 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400';
                      Icon = Award;
                      badgeClass = 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-455 dark:border-emerald-900/30';
                    } else if (isOffered) {
                      bgIcon = 'bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400';
                      Icon = Target;
                      badgeClass = 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-455 dark:border-blue-900/30';
                    } else if (isInterviewing) {
                      bgIcon = 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-455';
                      Icon = Clock;
                      badgeClass = 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-455 dark:border-amber-900/30';
                    }

                    return (
                      <div key={index} className="relative pl-8">
                        <div className={`absolute -left-3.5 top-0 w-7 h-7 rounded-full flex items-center justify-center border-4 border-white dark:border-[#002147] shadow-sm ${bgIcon}`}>
                          <Icon className="w-3 h-3" />
                        </div>
                        <div className="bg-gray-50 dark:bg-[#001f40] border border-gray-100 dark:border-[#1a3884]/15 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <h5 className="font-bold text-gray-900 dark:text-gray-250 text-sm">Applied to {app.jobPosting?.title || 'Role'}</h5>
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full shadow-sm border ${badgeClass} uppercase tracking-wider`}>
                              {badgeText}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-450">{app.jobPosting?.company?.name || 'Company Name N/A'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

// --- SUPER ADMIN QUICK LOOK COMPONENT ---
const SuperAdminQuickLook = ({ student, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState(null);
  const [currentPathIndex, setCurrentPathIndex] = useState(0);

  useEffect(() => {
    fetchStudentDetails();
  }, [student._id]);

  useEffect(() => {
    if (studentData?.placementProfile?.lockedCareerPaths?.length > 1) {
      const interval = setInterval(() => {
        setCurrentPathIndex(prev => (prev + 1) % studentData.placementProfile.lockedCareerPaths.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [studentData]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/student-progress/students/${student._id}`);
      if (response.data.success) {
        setStudentData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      toast.error('Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const completedCourses = studentData?.courseEnrollments?.filter(isCompletedCourseEnrollment) || [];
  const groupedCourses = completedCourses.reduce((acc, enrollment) => {
    const category = enrollment.course?.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(enrollment);
    return acc;
  }, {});

  const completedAssessments = studentData?.assessmentData?.filter(a => getAssessmentScore(a) != null || a.completedAt) || [];
  const detailStudent = studentData?.student || student;
  const lastLoginDate = detailStudent?.lastLogin ? new Date(detailStudent.lastLogin) : null;
  const hasValidLastLogin = lastLoginDate && !Number.isNaN(lastLoginDate.getTime());

  return createPortal(
    <div 
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-[#002147] rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col border border-gray-100 dark:border-[#1a3884]/20"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-[#00152E] to-[#1a3884] px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center font-black text-xl text-[#1a3884] shadow-md border-2 border-white/20">
              {student.fullName?.charAt(0) || '?'}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold leading-tight">{student.fullName || 'Unknown Student'}</h3>
                <span className="text-[10px] bg-white/20 border border-white/30 px-2 py-0.5 rounded-md font-semibold">
                  {detailStudent.college?.collegeName || detailStudent.college?.name || 'No Institute'}
                </span>
                <span className="text-[10px] bg-white/20 border border-white/30 px-2 py-0.5 rounded-md font-semibold font-mono">
                  ID: {student.studentId || student.rollNumber || '—'}
                </span>
              </div>
              <p className="text-white/80 text-xs mt-0.5">{student.email || 'No email provided'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-xl transition-colors text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 bg-slate-50 dark:bg-[#001f40] max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884] dark:border-[#4f7dff]" />
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Engagement Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-[#002147] p-4 rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 flex flex-col justify-center overflow-hidden">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-2">Last Login</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {hasValidLastLogin ? (
                      <>
                        <span className="text-base font-bold text-gray-900 dark:text-white">
                          {lastLoginDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="bg-blue-50 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] px-2.5 py-0.5 rounded-md text-[11px] font-bold border border-blue-100 dark:border-[#1a3884]/30 flex-shrink-0">
                          {lastLoginDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm font-black text-gray-400 dark:text-gray-500">Never</span>
                    )}
                  </div>
                </div>
                <div className="bg-white dark:bg-[#002147] p-4 rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 flex flex-col justify-center overflow-hidden">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Career Focus</p>
                    {studentData?.placementProfile?.lockedCareerPaths?.length > 1 && (
                      <div className="flex gap-1">
                        {studentData.placementProfile.lockedCareerPaths.map((_, i) => (
                          <div 
                            key={i} 
                            className={`h-1.5 rounded-full transition-all duration-500 ${i === currentPathIndex ? 'bg-[#1a3884] dark:bg-[#4f7dff] w-3' : 'bg-gray-200 dark:bg-gray-700 w-1.5'}`} 
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 relative h-[24px] w-full">
                    {studentData?.placementProfile?.lockedCareerPaths?.length > 0 ? (
                      <span 
                        key={currentPathIndex} // Forces React to re-mount and trigger entry animation
                        className="absolute left-0 top-0 max-w-full inline-block bg-blue-50 text-[#1a3884] dark:bg-blue-900/30 dark:text-[#4f7dff] px-2 py-0.5 rounded text-[11px] font-bold border border-blue-100 dark:border-blue-800/50 truncate animate-in fade-in slide-in-from-right-2 duration-500"
                        title={studentData.placementProfile.lockedCareerPaths[currentPathIndex]}
                      >
                        {studentData.placementProfile.lockedCareerPaths[currentPathIndex]}
                      </span>
                    ) : (
                      <span className="text-sm font-black text-gray-400 dark:text-gray-500 absolute left-0 top-0">Undecided</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Course Progress */}
              <div className="bg-white dark:bg-[#002147] p-5 rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    SMAART Courses Completed
                  </h4>
                  {completedCourses.length > 0 && (
                    <span className="bg-blue-50 text-[#1a3884] dark:bg-blue-900/30 dark:text-[#4f7dff] text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-100 dark:border-blue-800/50">
                      {completedCourses.length} Total
                    </span>
                  )}
                </div>
                
                {completedCourses.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto pr-2 space-y-5 custom-scrollbar">
                    {Object.entries(groupedCourses).map(([category, enrollments]) => (
                      <div key={category} className="space-y-2">
                        <h5 className="text-[10px] font-bold text-gray-400 dark:text-[#8bafff]/70 uppercase tracking-wider flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1a3884] dark:bg-[#4f7dff]"></span>
                          {category} 
                          <span className="text-gray-300 dark:text-[#4f7dff]/50">({enrollments.length})</span>
                        </h5>
                        <ul className="space-y-2 pl-3 border-l-2 border-gray-100 dark:border-[#1a3884]/30 ml-0.5">
                          {enrollments.map((enrollment, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-[#f1f5f9] bg-slate-50 dark:bg-[#00152E]/80 p-2 rounded-lg border border-slate-100 dark:border-[#1a3884]/30 transition-colors hover:dark:border-[#4f7dff]/40">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                              <span className="font-medium truncate">{enrollment.course?.title || 'Unknown Course'}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-[#8bafff]/50 italic">No courses completed yet.</p>
                )}
              </div>

              {/* Assessments */}
              <div className="bg-white dark:bg-[#002147] p-5 rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-500" />
                    Assessments Completed
                  </h4>
                  {completedAssessments.length > 0 && (
                    <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-100 dark:border-emerald-800/50">
                      {completedAssessments.length} Total
                    </span>
                  )}
                </div>
                {completedAssessments.length > 0 ? (
                  <div className="max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                    <ul className="space-y-2">
                      {completedAssessments.map((ast, idx) => {
                        const score = getAssessmentScore(ast);
                        const isPassing = (score || 0) >= 60 || ast.passed === true;
                        return (
                          <li key={idx} className="flex items-center justify-between text-sm text-gray-700 dark:text-[#f1f5f9] bg-slate-50 dark:bg-[#00152E]/80 p-2.5 rounded-lg border border-slate-100 dark:border-[#1a3884]/30 transition-colors hover:dark:border-[#4f7dff]/40">
                            <div className="flex items-center gap-2">
                              {isPassing ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-red-400 flex items-center justify-center flex-shrink-0 text-[10px] text-red-500 font-bold">!</div>
                              )}
                              <span className="font-medium truncate">{getAssessmentName(ast)}</span>
                            </div>
                            <span className={`font-bold px-2 py-0.5 rounded flex-shrink-0 ml-2 ${
                              isPassing 
                                ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' 
                                : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                            }`}>
                              {score ?? 0}%
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-[#8bafff]/50 italic">No assessments taken yet.</p>
                )}
              </div>

            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default StudentProgress;
